import React, { useEffect, useState } from "react";
import { RefreshCw, Search, Activity, Cookie, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const METHODS = ["ALL", "GET", "POST", "PATCH", "PUT", "DELETE"];
const STATUS_COLOR = (s) =>
  s >= 500 ? "text-red-300" : s >= 400 ? "text-amber-300" : s >= 300 ? "text-blue-300" : "text-emerald-300";

export function ModLogs() {
  const [tab, setTab] = useState("logs");
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [consents, setConsents] = useState([]);
  const [cStats, setCStats] = useState({ total: 0, accepted: 0 });
  const [method, setMethod] = useState("ALL");
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(false);

  const loadLogs = () => {
    setLoading(true);
    const params = { limit: 300 };
    if (method !== "ALL") params.method = method;
    if (q.trim()) params.q = q.trim();
    if (country.trim()) params.country = country.trim();
    api.get("/admin/logs", { params })
      .then((r) => { setLogs(r.data.logs); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  };
  const loadConsents = () => {
    api.get("/admin/consents", { params: { limit: 300 } })
      .then((r) => { setConsents(r.data.consents); setCStats({ total: r.data.total, accepted: r.data.accepted }); });
  };

  useEffect(() => { loadLogs(); loadConsents(); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="mod-logs">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-zinc-50">Tevékenységi napló</h2>
          <p className="text-[12px] text-zinc-500">Minden beérkező API-kérés és cookie-hozzájárulás (utolsó 5000 / 30 nap).</p>
        </div>
        <Button data-testid="logs-refresh" onClick={() => { loadLogs(); loadConsents(); }} variant="outline" className="h-9 gap-1.5 border-zinc-800 bg-zinc-900 text-[13px] text-zinc-200 hover:bg-zinc-800">
          <RefreshCw size={14} /> Frissítés
        </Button>
      </div>

      <div className="mb-4 flex gap-1 rounded-lg border border-zinc-800 bg-zinc-950 p-0.5">
        <button data-testid="logs-tab-requests" onClick={() => setTab("logs")} className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${tab === "logs" ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200"}`}>
          <Activity size={14} /> Kérések ({total})
        </button>
        <button data-testid="logs-tab-consents" onClick={() => setTab("consents")} className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${tab === "consents" ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200"}`}>
          <Cookie size={14} /> Hozzájárulások ({cStats.accepted}/{cStats.total})
        </button>
      </div>

      {tab === "logs" ? (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="logs-method" className="h-9 w-28 border-zinc-800 bg-zinc-950 text-[13px]"><SelectValue /></SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">
                {METHODS.map((m) => <SelectItem key={m} value={m} className="text-[13px] focus:bg-zinc-800">{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[160px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input data-testid="logs-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadLogs()} placeholder="Útvonal keresése…" className="h-9 border-zinc-800 bg-zinc-950 pl-9 text-[13px]" />
            </div>
            <Input data-testid="logs-country" value={country} onChange={(e) => setCountry(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadLogs()} placeholder="Ország (HU)" className="h-9 w-28 border-zinc-800 bg-zinc-950 text-[13px]" />
            <Button data-testid="logs-apply" onClick={loadLogs} className="h-9 bg-blue-600 px-4 text-[13px] text-white hover:bg-blue-500">Szűrés</Button>
          </div>

          <div className="overflow-hidden rounded-lg border border-zinc-800/70 bg-[#111113]">
            <table className="w-full text-left text-[12px]">
              <thead>
                <tr className="border-b border-zinc-800/70 text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="px-3 py-2 font-semibold">Idő (UTC)</th>
                  <th className="px-3 py-2 font-semibold">Metódus</th>
                  <th className="px-3 py-2 font-semibold">Útvonal</th>
                  <th className="px-3 py-2 font-semibold">Státusz</th>
                  <th className="px-3 py-2 font-semibold">ms</th>
                  <th className="px-3 py-2 font-semibold">Ország</th>
                  <th className="px-3 py-2 font-semibold">IP</th>
                </tr>
              </thead>
              <tbody data-testid="logs-table-body">
                {loading && <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-600"><Loader2 className="mx-auto animate-spin" size={18} /></td></tr>}
                {!loading && logs.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-600">Nincs napló.</td></tr>}
                {!loading && logs.map((l, i) => (
                  <tr key={i} className="border-b border-zinc-800/40 last:border-0 hover:bg-zinc-900/40">
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono text-zinc-400">{(l.ts || "").replace("T", " ").slice(0, 19)}</td>
                    <td className="px-3 py-1.5 font-mono text-zinc-300">{l.method}</td>
                    <td className="max-w-[260px] truncate px-3 py-1.5 font-mono text-zinc-300" title={l.path}>{l.path}</td>
                    <td className={`px-3 py-1.5 font-mono font-semibold ${STATUS_COLOR(l.status)}`}>{l.status}</td>
                    <td className="px-3 py-1.5 font-mono text-zinc-500">{l.ms}</td>
                    <td className="px-3 py-1.5 text-zinc-300">{l.country || "—"}</td>
                    <td className="max-w-[140px] truncate px-3 py-1.5 font-mono text-zinc-500" title={l.ua}>{l.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="overflow-hidden rounded-lg border border-zinc-800/70 bg-[#111113]">
          <table className="w-full text-left text-[12px]">
            <thead>
              <tr className="border-b border-zinc-800/70 text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="px-3 py-2 font-semibold">Idő (UTC)</th>
                <th className="px-3 py-2 font-semibold">Döntés</th>
                <th className="px-3 py-2 font-semibold">Ország</th>
                <th className="px-3 py-2 font-semibold">IP</th>
                <th className="px-3 py-2 font-semibold">User-agent</th>
              </tr>
            </thead>
            <tbody data-testid="consents-table-body">
              {consents.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-zinc-600">Nincs hozzájárulás.</td></tr>}
              {consents.map((c) => (
                <tr key={c.id} className="border-b border-zinc-800/40 last:border-0 hover:bg-zinc-900/40">
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-zinc-400">{(c.ts || "").replace("T", " ").slice(0, 19)}</td>
                  <td className="px-3 py-1.5">
                    <span className={`rounded border px-1.5 py-0.5 text-[10px] ${c.accepted ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-red-500/40 bg-red-500/10 text-red-300"}`}>{c.accepted ? "Elfogadta" : "Elutasította"}</span>
                  </td>
                  <td className="px-3 py-1.5 text-zinc-300">{c.country || "—"}</td>
                  <td className="max-w-[140px] truncate px-3 py-1.5 font-mono text-zinc-500">{c.ip || "—"}</td>
                  <td className="max-w-[320px] truncate px-3 py-1.5 text-zinc-500" title={c.ua}>{c.ua || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
